import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    CalendarClock,
    Check,
    ClipboardList,
    Coffee,
    FileWarning,
    Inbox,
    IdCard,
    ReceiptText,
    TimerReset,
    X,
    Search,
    MessageSquareWarning,
    LogOut,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../api/axios";
import AdminNavbar from "../../components/layout/AdminNavbar";

const todayKey = () => new Date().toISOString().split("T")[0];

const formatDate = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const formatShortDate = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
    });
};

const formatTime = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleTimeString("en-IN", {
        timeZone: "Asia/Kolkata",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const getCorrectionTiming = (request) => {
    const hasRequestedTiming = request?.requestedCheckIn || request?.requestedCheckOut;
    const checkIn = hasRequestedTiming ? request.requestedCheckIn : request?.attendanceSnapshot?.checkIn;
    const checkOut = hasRequestedTiming ? request.requestedCheckOut : request?.attendanceSnapshot?.checkOut;
    const mode = request?.requestedMode || request?.attendanceSnapshot?.mode || "office";

    if (!checkIn && !checkOut) {
        return {
            label: "Timing not provided",
            value: mode,
        };
    }

    return {
        label: hasRequestedTiming ? "Requested timing" : "Current timing",
        value: `${formatTime(checkIn)} - ${formatTime(checkOut)} - ${mode}`,
    };
};

const employeeLabel = (employee) => employee?.name || employee?.email || "Employee";

const itemKey = (prefix, item, index) => {
    const id = item?._id || item?.id || item?.employee?._id || item?.employee || item?.employeeId;
    return `${prefix}-${id || index}`;
};

const formatRoleLabel = (role = "") => {
    const value = String(role || "").trim();
    if (!value) return "";
    return value
        .split(/\s+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
};

const reviewerLabel = (person) => {
    if (!person) return "";
    const role = formatRoleLabel(person.role) || person.team || "";
    return `${person.name || person.email || "Admin"}${role ? ` (${role})` : ""}`;
};

const EmptyState = ({ children }) => (
    <div className="border border-dashed border-[var(--border-color)] bg-[var(--bg-secondary)] rounded-2xl p-6 text-center text-sm text-[var(--text-secondary)]">
        {children}
    </div>
);

const Section = ({ icon, title, count, children }) => (
    <section className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-2xl shadow-[var(--shadow-soft)] p-5 space-y-4">
        <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] flex items-center justify-center shrink-0">
                    {icon}
                </div>
                <h2 className="font-bold text-lg truncate">{title}</h2>
            </div>
            <span className="min-w-9 h-8 px-3 rounded-full bg-[var(--bg-secondary)] border border-[var(--border-color)] flex items-center justify-center text-sm font-bold">
                {count}
            </span>
        </div>
        {children}
    </section>
);

const ActionButtons = ({ onApprove, onReject, disabled }) => (
    <div className="flex items-center gap-2">
        <button
            type="button"
            onClick={onApprove}
            disabled={disabled}
            className="w-9 h-9 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center disabled:opacity-60"
            title="Approve"
        >
            <Check size={16} />
        </button>
        <button
            type="button"
            onClick={onReject}
            disabled={disabled}
            className="w-9 h-9 rounded-xl bg-red-500 hover:bg-red-600 text-white flex items-center justify-center disabled:opacity-60"
            title="Reject"
        >
            <X size={16} />
        </button>
    </div>
);

const historyThemes = {
    approved: {
        label: "Approved requests",
        singular: "approved request",
        empty: "No approved requests yet.",
        cardBorder: "border-emerald-200",
        cardBg: "bg-emerald-50/40",
        hoverBorder: "hover:border-emerald-300",
        hoverBg: "hover:bg-emerald-50",
        focusRing: "focus:ring-emerald-200",
        text: "text-emerald-800",
        mutedText: "text-emerald-700",
        topBar: "bg-emerald-500",
        closeBg: "bg-emerald-50",
        closeText: "text-emerald-700",
        closeHover: "hover:bg-emerald-100",
    },
    rejected: {
        label: "Rejected requests",
        singular: "rejected request",
        empty: "No rejected requests yet.",
        cardBorder: "border-red-200",
        cardBg: "bg-red-50/40",
        hoverBorder: "hover:border-red-300",
        hoverBg: "hover:bg-red-50",
        focusRing: "focus:ring-red-200",
        text: "text-red-800",
        mutedText: "text-red-700",
        topBar: "bg-red-500",
        closeBg: "bg-red-50",
        closeText: "text-red-700",
        closeHover: "hover:bg-red-100",
    },
};

const isRejectedHistoryItem = (request) => {
    const status = String(request?.status || "").toLowerCase();
    return ["rejected", "dismissed"].includes(status);
};

const RequestHistoryButton = ({ requests, variant, onOpen }) => {
    const theme = historyThemes[variant];

    if (!requests.length) {
        return <EmptyState>{theme.empty}</EmptyState>;
    }

    return (
        <button
            type="button"
            onClick={onOpen}
            className={`w-full rounded-2xl border ${theme.cardBorder} ${theme.cardBg} p-5 text-left transition-all hover:-translate-y-0.5 ${theme.hoverBorder} ${theme.hoverBg} focus:outline-none focus:ring-2 ${theme.focusRing}`}
        >
            <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                    <p className={`text-base font-black ${theme.text}`}>{theme.label}</p>
                    <p className={`mt-1 text-xs font-bold ${theme.mutedText}`}>Touch to view data</p>
                </div>
                <span className={`shrink-0 rounded-full border ${theme.cardBorder} bg-[var(--card-bg)] px-3 py-1 text-sm font-black ${theme.mutedText}`}>
                    {requests.length}
                </span>
            </div>
        </button>
    );
};

const RequestHistoryDrawer = ({ open, onClose, requests, variant }) => {
    const theme = historyThemes[variant];
    const adminLabel = variant === "approved" ? "Approved by" : "Rejected by";

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
                initial={{ scale: 0.95, opacity: 0, y: 15 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.95, opacity: 0, y: 15 }}
                transition={{ type: "spring", duration: 0.35 }}
                className={`relative z-10 flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl border ${theme.cardBorder} bg-[var(--card-bg)] p-6 shadow-2xl`}
            >
                <div className={`absolute left-0 top-0 h-1 w-full rounded-t-3xl ${theme.topBar}`} />
                <div className="flex items-start justify-between gap-4 border-b border-[var(--border-color)] pb-4">
                    <div>
                        <p className={`text-xs font-black uppercase tracking-wide ${theme.mutedText}`}>{theme.label}</p>
                        <h3 className="mt-1 text-xl font-black">
                            {requests.length} {theme.singular}{requests.length === 1 ? "" : "s"}
                        </h3>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className={`rounded-xl ${theme.closeBg} p-2 ${theme.closeText} transition ${theme.closeHover}`}
                        title="Close"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="mt-5 flex-1 space-y-3 overflow-y-auto pr-1">
                    {requests.map((request, index) => (
                        <div key={itemKey(variant, request, index)} className={`rounded-2xl border ${theme.cardBorder} ${theme.cardBg} p-4`}>
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="break-words font-black">{employeeLabel(request.employee)}</p>
                                    <p className="mt-1 break-words text-xs font-bold text-[var(--text-secondary)]">
                                        {request.employee?.team || "No team"} - {formatDate(request.date || request.approvedAt)}
                                    </p>
                                </div>
                                <span className={`rounded-full border ${theme.cardBorder} bg-[var(--card-bg)] px-2 py-0.5 text-[10px] font-black uppercase ${theme.mutedText}`}>
                                    {request.status}
                                </span>
                            </div>
                            <div className="mt-3 grid gap-3 sm:grid-cols-2">
                                <div className="rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)]/80 px-3 py-2">
                                    <p className="text-[10px] font-black uppercase tracking-wide text-[var(--text-secondary)]">Request type</p>
                                    <p className="mt-1 break-words text-sm font-black">{request.type}</p>
                                    <p className="mt-1 break-words text-xs font-bold text-[var(--text-secondary)]">{request.title || "Request"}</p>
                                </div>
                                <div className={`rounded-xl border ${theme.cardBorder} bg-[var(--card-bg)]/80 px-3 py-2`}>
                                    <p className={`text-[10px] font-black uppercase tracking-wide ${theme.mutedText}`}>Admin</p>
                                    <p className={`mt-1 break-words text-sm font-black ${theme.text}`}>{reviewerLabel(request.approvedBy) || "Admin"}</p>
                                    <p className={`mt-1 text-xs font-bold ${theme.mutedText}`}>{request.approvedAt ? formatDate(request.approvedAt) : "-"}</p>
                                </div>
                            </div>
                            <div className="mt-3 rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)]/80 px-3 py-2">
                                <p className="text-[10px] font-black uppercase tracking-wide text-[var(--text-secondary)]">
                                    {variant === "approved" ? "Details" : "Reason / details"}
                                </p>
                                <p className="mt-1 whitespace-pre-wrap break-words text-sm font-semibold leading-relaxed">
                                    {request.details || `${adminLabel} without extra details.`}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </motion.div>
        </div>
    );
};

const ApprovalInbox = () => {
    const navigate = useNavigate();
    const [date, setDate] = useState(todayKey());
    const [inbox, setInbox] = useState(null);
    const [loading, setLoading] = useState(true);
    const [processingId, setProcessingId] = useState("");
    const [actionIndicator, setActionIndicator] = useState("");

    // Missing EOD Modal State
    const [activeModalSection, setActiveModalSection] = useState(null);
    const [isMissingEodModalOpen, setIsMissingEodModalOpen] = useState(false);
    const [missingEodSearch, setMissingEodSearch] = useState("");
    const [missingEodFilterType, setMissingEodFilterType] = useState("selected"); // "selected" or "custom"
    const [missingEodCustomDate, setMissingEodCustomDate] = useState("");
    const [selectedApprovedRequest, setSelectedApprovedRequest] = useState(null);
    const [isApprovedRequestsOpen, setIsApprovedRequestsOpen] = useState(false);
    const [isRejectedRequestsOpen, setIsRejectedRequestsOpen] = useState(false);

    const fetchInbox = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get("/api/admin/approval-inbox", { params: { date } });
            setInbox(res.data);
        } catch (err) {
            if ([401, 403].includes(err.response?.status)) navigate("/admin/login");
        } finally {
            setLoading(false);
        }
    }, [date, navigate]);

    useEffect(() => {
        document.title = "Approval Inbox | Savrion Technologies";
        fetchInbox();
    }, [fetchInbox]);

    const counts = useMemo(() => inbox?.counts || {
        pendingLeaves: 0,
        pendingExpenses: 0,
        attendanceCorrections: 0,
        profileChangeRequests: 0,
        payslipIssues: 0,
        employeeRequests: 0,
        offboardingRequests: 0,
        earlyEodRequests: 0,
        lunchUnlockRequests: 0,
        missingEod: 0,
    }, [inbox]);

    const totalPending = counts.pendingLeaves
        + counts.pendingExpenses
        + counts.attendanceCorrections
        + counts.profileChangeRequests
        + counts.payslipIssues
        + counts.employeeRequests
        + counts.offboardingRequests
        + (counts.lunchUnlockRequests || 0)
        + counts.missingEod;

    const handleLunchUnlock = async (reqItem, status) => {
        const rejectionReason = status === "rejected" ? window.prompt("Reason for rejection:") : "";
        if (status === "rejected" && rejectionReason === null) return;

        const previousInbox = inbox;
        setInbox((current) => {
            if (!current) return current;
            return {
                ...current,
                lunchUnlockRequests: (current.lunchUnlockRequests || []).filter((item) => item._id !== reqItem._id),
                counts: {
                    ...(current.counts || {}),
                    lunchUnlockRequests: Math.max(0, Number(current.counts?.lunchUnlockRequests || 0) - 1),
                },
            };
        });
        setProcessingId(reqItem._id);
        setActionIndicator(status === "approved" ? "Lunch break unlocked" : "Lunch unlock rejected");
        window.setTimeout(() => setActionIndicator(""), 2200);
        try {
            if (status === "approved") {
                await api.post(`/api/admin/attendance/break-unlock/${reqItem._id}/approve`, {}, { skipSaveToast: true });
            } else {
                await api.post(`/api/admin/attendance/break-unlock/${reqItem._id}/reject`, { rejectionReason }, { skipSaveToast: true });
            }
        } catch (err) {
            setInbox(previousInbox);
            setActionIndicator("Failed to process lunch unlock");
            alert(err.response?.data?.message || "Failed to process lunch unlock request");
        } finally {
            setProcessingId("");
        }
    };

    const handleLeave = async (leave, status) => {
        const rejectionReason = status === "rejected" ? window.prompt("Reason for rejection:") : "";
        if (rejectionReason === null) return;

        const previousInbox = inbox;
        setInbox((current) => {
            if (!current) return current;
            return {
                ...current,
                pendingLeaves: (current.pendingLeaves || []).filter((item) => item._id !== leave._id),
                counts: {
                    ...(current.counts || {}),
                    pendingLeaves: Math.max(0, Number(current.counts?.pendingLeaves || 0) - 1),
                },
            };
        });
        setProcessingId(leave._id);
        setActionIndicator(status === "approved" ? "Leave approved" : "Leave rejected");
        window.setTimeout(() => setActionIndicator(""), 2200);
        try {
            await api.patch(
                `/api/admin/leave/${leave._id}`,
                { status, rejectionReason },
                { skipSaveToast: true },
            );
        } catch (err) {
            setInbox(previousInbox);
            setActionIndicator("Failed to update leave");
            alert(err.response?.data?.message || "Failed to update leave request");
        } finally {
            setProcessingId("");
        }
    };

    const handleExpense = async (claim, status) => {
        const rejectionReason = status === "Rejected" ? window.prompt("Reason for rejection:") : "";
        if (rejectionReason === null) return;

        setProcessingId(claim._id);
        try {
            await api.patch(`/api/admin/expenses/${claim._id}/status`, { status, rejectionReason });
            await fetchInbox();
        } catch (err) {
            alert(err.response?.data?.message || "Failed to update expense claim");
        } finally {
            setProcessingId("");
        }
    };

    const handleCorrection = async (request, status) => {
        const rejectionReason = status === "rejected" ? window.prompt("Reason for rejection:") : "";
        if (rejectionReason === null) return;

        setProcessingId(request._id);
        try {
            const res = await api.patch(`/api/admin/attendance-corrections/${request._id}`, { status, rejectionReason });
            const updatedCorrection = res.data?.correction;
            setInbox((current) => ({
                ...current,
                attendanceCorrections: (current?.attendanceCorrections || []).map((item) =>
                    item._id === request._id ? { ...item, ...(updatedCorrection || {}), attendanceSnapshot: item.attendanceSnapshot } : item,
                ),
            }));
            fetchInbox().catch(() => {});
        } catch (err) {
            alert(err.response?.data?.message || "Failed to update attendance correction");
        } finally {
            setProcessingId("");
        }
    };

    const handleProfileChange = async (request, status) => {
        const adminComment = window.prompt(status === "approved" ? "Approval comment (optional):" : "Reason for rejection:");
        if (adminComment === null) return;

        setProcessingId(request._id);
        try {
            await api.patch(`/api/admin/profile-change-requests/${request._id}`, { status, adminComment });
            await fetchInbox();
        } catch (err) {
            alert(err.response?.data?.message || "Failed to update profile change request");
        } finally {
            setProcessingId("");
        }
    };

    const handlePayslipIssue = async (issue, status) => {
        const adminComment = window.prompt(status === "resolved" ? "Resolution comment:" : "Reason for rejection:");
        if (adminComment === null) return;

        setProcessingId(issue._id);
        try {
            await api.patch(`/api/admin/payslip-issues/${issue._id}`, { status, adminComment });
            await fetchInbox();
        } catch (err) {
            alert(err.response?.data?.message || "Failed to update payslip issue");
        } finally {
            setProcessingId("");
        }
    };

    const filteredMissingEods = useMemo(() => {
        const query = missingEodSearch.toLowerCase();
        const targetDate = missingEodFilterType === "selected" ? date : missingEodCustomDate;

        return (inbox?.missingEod || []).filter(emp => {
            // Check if employee has missing EOD for the target date
            const isMissingOnDate = (emp.missingDates || []).includes(targetDate);
            if (!isMissingOnDate) return false;

            return (
                emp.name?.toLowerCase().includes(query) ||
                emp.team?.toLowerCase().includes(query) ||
                emp.employeeId?.toLowerCase().includes(query) ||
                emp.email?.toLowerCase().includes(query)
            );
        });
    }, [inbox?.missingEod, missingEodSearch, missingEodFilterType, missingEodCustomDate, date]);

    const openAttendanceCorrections = useMemo(
        () => (inbox?.attendanceCorrections || []).filter((request) => ["pending", "pending_lead"].includes(request.status)),
        [inbox?.attendanceCorrections],
    );

    const requestHistory = useMemo(
        () => inbox?.approvedRequests || [],
        [inbox?.approvedRequests],
    );
    const approvedRequests = useMemo(
        () => requestHistory.filter((request) => !isRejectedHistoryItem(request)),
        [requestHistory],
    );
    const rejectedRequests = useMemo(
        () => requestHistory.filter(isRejectedHistoryItem),
        [requestHistory],
    );

    return (
        <div className="flex flex-col xl:flex-row min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]">
            <AdminNavbar />
            <main className="flex-1 min-w-0 p-4 sm:p-6 space-y-6">
                {actionIndicator && (
                    <div className={`rounded-2xl border px-4 py-3 text-sm font-bold ${
                        actionIndicator.startsWith("Failed")
                            ? "border-red-500/20 bg-red-500/10 text-red-500"
                            : "border-emerald-500/20 bg-emerald-500/10 text-emerald-600"
                    }`}>
                        {actionIndicator}
                    </div>
                )}
                <div className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-2xl shadow-[var(--shadow-soft)] p-5 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
                    <div className="flex items-start gap-4 min-w-0">
                        <div className="w-12 h-12 rounded-2xl bg-[var(--accent-primary)] text-white flex items-center justify-center shrink-0">
                            <Inbox size={22} />
                        </div>
                        <div className="min-w-0">
                            <p className="text-xs font-bold uppercase tracking-wide text-[var(--accent-primary)]">Approval Inbox</p>
                            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Pending admin work</h1>
                            <p className="mt-1 text-sm text-[var(--text-secondary)]">
                                {loading ? "Loading requests..." : `${totalPending} item${totalPending === 1 ? "" : "s"} need attention`}
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <label className="flex items-center gap-2 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-xl px-3 py-2">
                            <CalendarClock size={17} className="text-[var(--text-secondary)]" />
                            <input
                                type="date"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                className="bg-transparent outline-none text-sm font-semibold"
                            />
                        </label>
                    </div>
                </div>

                {/* NOTICEABLE MISSING EOD ALERT CONTAINER */}
                {counts.missingEod > 0 && (
                    <motion.div 
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-gradient-to-r from-red-500/10 to-amber-500/10 border border-red-500/20 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 shadow-sm"
                    >
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-red-500/20 text-red-500 flex items-center justify-center shrink-0">
                                <FileWarning size={24} className="animate-pulse" />
                            </div>
                            <div className="space-y-1">
                                <h3 className="font-extrabold text-base text-[var(--text-primary)]">Action Required: Missing EOD Reports</h3>
                                <p className="text-xs text-[var(--text-secondary)] font-semibold leading-relaxed">
                                    There are <span className="font-black text-red-500">{counts.missingEod} employees</span> who have not submitted their EOD reports for the date: <span className="font-black text-[var(--text-primary)] underline decoration-red-500">{formatDate(date)}</span>.
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => {
                                setMissingEodSearch("");
                                setMissingEodFilterType("selected");
                                setMissingEodCustomDate(date);
                                setIsMissingEodModalOpen(true);
                            }}
                            className="h-10 px-5 rounded-xl bg-red-500 hover:bg-red-600 hover:shadow-md hover:shadow-red-500/20 text-white font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 shrink-0 transition-all hover:-translate-y-0.5 active:scale-[0.98] duration-200"
                        >
                            Review Missing Reports
                        </button>
                    </motion.div>
                )}

                <div className="grid grid-cols-2 lg:grid-cols-4 2xl:grid-cols-6 gap-4">
                    {[
                        ["Leaves", counts.pendingLeaves, <ClipboardList size={18} />],
                        ["Expenses", counts.pendingExpenses, <ReceiptText size={18} />],
                        ["Corrections", counts.attendanceCorrections, <TimerReset size={18} />],
                        ["Lunch Unlock", counts.lunchUnlockRequests || 0, <Coffee size={18} />],
                        ["Profile", counts.profileChangeRequests, <IdCard size={18} />],
                        ["Payslip", counts.payslipIssues, <FileWarning size={18} />],
                        ["Requests", counts.employeeRequests, <MessageSquareWarning size={18} />],
                        ["Offboarding", counts.offboardingRequests, <LogOut size={18} />],
                        ["Early EOD", counts.earlyEodRequests || 0, <CalendarClock size={18} />],
                        ["Approved", approvedRequests.length, <Check size={18} />],
                        ["Rejected", rejectedRequests.length, <X size={18} />],
                        ["Missing EOD", counts.missingEod, <FileWarning size={18} />],
                    ].map(([label, value, icon]) => {
                        const isMissingEod = label === "Missing EOD";
                        const hasItems = value > 0 && !["Approved", "Rejected"].includes(label);
                        return (
                            <div 
                                key={label} 
                                onClick={() => {
                                    if (isMissingEod) {
                                        setMissingEodSearch("");
                                        setMissingEodFilterType("selected");
                                        setMissingEodCustomDate(date);
                                        setIsMissingEodModalOpen(true);
                                    } else {
                                        setActiveModalSection(label);
                                    }
                                }}
                                className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-3xl p-5 flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-[var(--accent-primary)]/50 hover:shadow-lg hover:-translate-y-1 active:scale-[0.98] transition-all duration-300 relative text-center"
                            >
                                {hasItems && !isMissingEod && (
                                    <span className="absolute -top-1.5 -right-1.5 flex h-3 w-3">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                                    </span>
                                )}
                                <div className="w-14 h-14 rounded-2xl bg-[var(--accent-primary)]/5 text-[var(--accent-primary)] flex items-center justify-center shadow-sm">
                                    {icon}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-xs text-[var(--text-secondary)] flex items-center gap-1.5 font-semibold">
                                        {isMissingEod ? `Missing EOD (${formatShortDate(date)})` : label}
                                        {isMissingEod && (
                                            <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent-primary)] animate-pulse shrink-0" title="Click to view details"></span>
                                        )}
                                    </p>
                                    <p className="text-xl font-bold">{value}</p>
                                </div>
                            </div>
                        );
                    })}
                </div>

                                <AnimatePresence>
                    {activeModalSection && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                            <motion.div 
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                onClick={() => setActiveModalSection(null)}
                                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                            />
                            <motion.div 
                                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                                animate={{ scale: 1, opacity: 1, y: 0 }}
                                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                                className="bg-[var(--card-bg)] border border-[var(--border-color)] w-full max-w-3xl rounded-[32px] p-2 shadow-2xl z-10 relative flex flex-col max-h-[85vh] overflow-hidden"
                            >
                                <button 
                                    onClick={() => setActiveModalSection(null)}
                                    className="absolute top-5 right-5 p-2.5 rounded-full hover:bg-[var(--bg-secondary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-all z-20"
                                >
                                    <X size={22} />
                                </button>
                                <div className="flex-1 overflow-y-auto custom-scrollbar p-2">
                                    <div className="flex flex-col [&>section]:shadow-none [&>section]:border-none [&>section]:bg-transparent [&>section]:p-6 [&>section>div:first-child]:mb-6 [&>section>div:first-child>span]:hidden [&>section>div:first-child_h2]:text-2xl">
                    {activeModalSection === 'Leaves' && <Section icon={<ClipboardList size={18} />} title="Pending Leave Requests" count={counts.pendingLeaves}>
                        {!inbox?.pendingLeaves?.length ? (
                            <EmptyState>No pending leave requests.</EmptyState>
                        ) : (
                            <div className="space-y-3">
                                {inbox.pendingLeaves.map((leave, index) => (
                                    <div key={itemKey("pending-leave", leave, index)} className="border border-[var(--border-color)] rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                        <div className="min-w-0">
                                            <p className="font-bold truncate">{employeeLabel(leave.employee)}</p>
                                            <p className="text-xs text-[var(--text-secondary)] truncate">{leave.employee?.team || "No team"} - {leave.leaveType}</p>
                                            <p className="text-sm mt-2">{formatDate(leave.startDate)} - {formatDate(leave.endDate)}</p>
                                            <p className="text-xs text-[var(--text-secondary)] mt-1 line-clamp-2">{leave.reason}</p>
                                        </div>
                                        <ActionButtons
                                            disabled={processingId === leave._id}
                                            onApprove={() => handleLeave(leave, "approved")}
                                            onReject={() => handleLeave(leave, "rejected")}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </Section>}
{activeModalSection === 'Expenses' && <Section icon={<ReceiptText size={18} />} title="Pending Expense Claims" count={counts.pendingExpenses}>
                        {!inbox?.pendingExpenses?.length ? (
                            <EmptyState>No pending expense claims.</EmptyState>
                        ) : (
                            <div className="space-y-3">
                                {inbox.pendingExpenses.map((claim, index) => (
                                    <div key={itemKey("pending-expense", claim, index)} className="border border-[var(--border-color)] rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                        <div className="min-w-0">
                                            <p className="font-bold truncate">{employeeLabel(claim.employee)}</p>
                                            <p className="text-xs text-[var(--text-secondary)] truncate">{claim.employee?.team || "No team"} - {formatDate(claim.date)}</p>
                                            <p className="text-sm mt-2 font-semibold">Rs. {Number(claim.amount || 0).toLocaleString("en-IN")} - {claim.title}</p>
                                            <p className="text-xs text-[var(--text-secondary)] mt-1 line-clamp-2">{claim.description || "No description"}</p>
                                        </div>
                                        <ActionButtons
                                            disabled={processingId === claim._id}
                                            onApprove={() => handleExpense(claim, "Approved")}
                                            onReject={() => handleExpense(claim, "Rejected")}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </Section>}
{activeModalSection === 'Corrections' && <Section icon={<TimerReset size={18} />} title="Attendance Correction Requests" count={counts.attendanceCorrections}>
                        {!openAttendanceCorrections.length ? (
                            <EmptyState>No pending attendance correction requests.</EmptyState>
                        ) : (
                            <div className="space-y-3">
                                {openAttendanceCorrections.map((request, index) => {
                                    const timing = getCorrectionTiming(request);
                                    return (
                                    <div key={itemKey("attendance-correction", request, index)} className="border border-[var(--border-color)] rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <p className="font-bold truncate">{employeeLabel(request.employee)}</p>
                                                {request.status === "pending_lead" && (
                                                    <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-black uppercase text-amber-700">
                                                        Waiting for lead
                                                    </span>
                                                )}
                                                {request.hrNotifiedAdminAt && (
                                                    <span className="rounded-full border border-[var(--accent-soft)] bg-[var(--accent-light)] px-2 py-0.5 text-[10px] font-black uppercase text-[var(--heading-secondary)]">
                                                        HR flagged
                                                    </span>
                                                )}
                                                {request.status === "approved" && (
                                                    <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-black uppercase text-emerald-700">
                                                        Approved
                                                    </span>
                                                )}
                                                {request.status === "rejected" && (
                                                    <span className="rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-black uppercase text-red-700">
                                                        Rejected
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-[var(--text-secondary)] truncate">{request.employee?.team || "No team"} - {formatDate(request.date)}</p>
                                            <p className="text-sm mt-2 font-semibold text-[var(--text-primary)]">
                                                <span className="text-xs font-black uppercase tracking-wide text-[var(--text-secondary)]">{timing.label}: </span>
                                                {timing.value}
                                            </p>
                                            <p className="text-xs text-[var(--text-secondary)] mt-1 line-clamp-2">{request.reason}</p>
                                            {request.hrNotifiedAdminAt && (
                                                <div className="mt-3 rounded-xl border border-[var(--accent-soft)] bg-[var(--accent-light)] px-3 py-2 text-xs font-bold text-[var(--heading-secondary)]">
                                                    HR message: This employee correction is still pending. Please see and rectify it.
                                                    {request.hrNotifiedAdminBy?.name ? ` - ${request.hrNotifiedAdminBy.name}` : ""}
                                                </div>
                                            )}
                                            {["approved", "rejected"].includes(request.status) && (
                                                <div className={`mt-3 rounded-xl border px-3 py-2 text-xs font-bold ${
                                                    request.status === "approved"
                                                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                                        : "border-red-200 bg-red-50 text-red-700"
                                                }`}>
                                                    {request.status === "approved" ? "Approved" : "Rejected"} by {reviewerLabel(request.reviewedBy) || "Admin"}
                                                    {request.reviewedAt ? ` on ${formatDate(request.reviewedAt)}` : ""}
                                                </div>
                                            )}
                                        </div>
                                        {["pending", "pending_lead"].includes(request.status) ? (
                                            <ActionButtons
                                                disabled={processingId === request._id}
                                                onApprove={() => handleCorrection(request, "approved")}
                                                onReject={() => handleCorrection(request, "rejected")}
                                            />
                                        ) : (
                                            <span className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 py-2 text-xs font-black text-[var(--text-secondary)]">
                                                {request.status === "approved" ? "Approved" : request.status === "rejected" ? "Rejected" : "Lead review pending"}
                                            </span>
                                        )}
                                    </div>
                                    );
                                })}
                            </div>
                        )}
                    </Section>}
{activeModalSection === 'Early EOD' && <Section icon={<CalendarClock size={18} />} title="Early EOD Requests" count={counts.earlyEodRequests || 0}>
                        {!inbox?.earlyEodRequests?.length ? (
                            <EmptyState>No early EOD requests for the selected date.</EmptyState>
                        ) : (
                            <div className="space-y-3">
                                {inbox.earlyEodRequests.map((request, index) => (
                                    <div
                                        key={itemKey("early-eod", request, index)}
                                        className="rounded-2xl border border-amber-200 bg-amber-50/40 p-4 dark:border-amber-500/20 dark:bg-amber-500/5"
                                    >
                                        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                                            <div className="min-w-0">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <p className="font-bold break-words">{employeeLabel(request.employee)}</p>
                                                    <span className="rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-[10px] font-black uppercase text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
                                                        Early EOD
                                                    </span>
                                                    <span className="rounded-full border border-[var(--border-color)] bg-[var(--card-bg)] px-2 py-0.5 text-[10px] font-black uppercase text-[var(--text-secondary)]">
                                                        {request.status || "draft"}
                                                    </span>
                                                </div>
                                                <p className="mt-1 text-xs text-[var(--text-secondary)] break-words">
                                                    {request.employee?.team || "No team"} - {formatDate(request.date)}
                                                    {request.earlyAccessReasonSubmittedAt ? ` at ${formatTime(request.earlyAccessReasonSubmittedAt)}` : ""}
                                                </p>
                                                <div className="mt-3 rounded-xl border border-amber-200/80 bg-[var(--card-bg)]/70 px-3 py-2.5 dark:border-amber-500/15 dark:bg-[var(--card-bg)]">
                                                    <p className="text-[10px] font-black uppercase tracking-wide text-amber-700 dark:text-amber-300">Reason</p>
                                                    <p className="mt-1 whitespace-pre-wrap break-words text-sm font-semibold leading-6 text-[var(--text-primary)]">
                                                        {request.earlyAccessReason}
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => navigate("/admin/eod")}
                                                className="h-9 shrink-0 rounded-xl border border-amber-200 bg-[var(--card-bg)] px-4 text-xs font-black uppercase tracking-wide text-amber-700 transition hover:bg-amber-100 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300"
                                            >
                                                View report
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Section>}
{activeModalSection === 'Requests' && <Section icon={<MessageSquareWarning size={18} />} title="Employee Requests" count={counts.employeeRequests}>
                        {!inbox?.employeeRequests?.length ? (
                            <EmptyState>No open employee requests, complaints, or grievances.</EmptyState>
                        ) : (
                            <div className="space-y-3">
                                {inbox.employeeRequests.map((item, index) => (
                                    <div key={itemKey("employee-request", item, index)} className="border border-[var(--border-color)] rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <p className="font-bold break-words">{employeeLabel(item.raisedBy)}</p>
                                                <span className="rounded-full border border-[var(--accent-soft)] bg-[var(--accent-light)] px-2 py-0.5 text-[10px] font-black uppercase text-[var(--heading-secondary)]">
                                                    {item.type || "Request"}
                                                </span>
                                                <span className="rounded-full border border-[var(--border-color)] bg-[var(--bg-secondary)] px-2 py-0.5 text-[10px] font-black uppercase text-[var(--text-secondary)]">
                                                    {item.status}
                                                </span>
                                            </div>
                                            <p className="text-xs text-[var(--text-secondary)] break-words">{item.raisedBy?.team || "No team"} - {formatDate(item.createdAt)}</p>
                                            <p className="text-sm mt-2 font-semibold break-words">{item.subject || item.type || "Employee request"}</p>
                                            <p className="text-xs text-[var(--text-secondary)] mt-1 line-clamp-2 break-words">{item.description}</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => navigate("/admin/complaints")}
                                            className="h-9 px-4 rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] text-xs font-black uppercase tracking-wide shrink-0"
                                        >
                                            Review
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Section>}
{activeModalSection === 'Approved' && <Section icon={<Check size={18} />} title="Approved Requests" count={approvedRequests.length}>
                        <RequestHistoryButton
                            requests={approvedRequests}
                            variant="approved"
                            onOpen={() => setIsApprovedRequestsOpen(true)}
                        />
                    </Section>}
{activeModalSection === 'Rejected' && <Section icon={<X size={18} />} title="Rejected Requests" count={rejectedRequests.length}>
                        <RequestHistoryButton
                            requests={rejectedRequests}
                            variant="rejected"
                            onOpen={() => setIsRejectedRequestsOpen(true)}
                        />
                    </Section>}
{activeModalSection === 'Profile' && <Section icon={<IdCard size={18} />} title="Profile Change Requests" count={counts.profileChangeRequests}>
                        {!inbox?.profileChangeRequests?.length ? (
                            <EmptyState>No pending profile change requests.</EmptyState>
                        ) : (
                            <div className="space-y-3">
                                {inbox.profileChangeRequests.map((request, index) => (
                                    <div key={itemKey("profile-change", request, index)} className="border border-[var(--border-color)] rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                        <div className="min-w-0">
                                            <p className="font-bold truncate">{employeeLabel(request.employee)}</p>
                                            <p className="text-xs text-[var(--text-secondary)] truncate">{request.employee?.team || "No team"} - {request.fieldLabel}</p>
                                            <p className="text-sm mt-2">Current: {request.currentValue || "-"}</p>
                                            <p className="text-sm font-semibold">Requested: {request.requestedValue}</p>
                                            {request.employeeNote && <p className="text-xs text-[var(--text-secondary)] mt-1 line-clamp-2">{request.employeeNote}</p>}
                                        </div>
                                        <ActionButtons
                                            disabled={processingId === request._id}
                                            onApprove={() => handleProfileChange(request, "approved")}
                                            onReject={() => handleProfileChange(request, "rejected")}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </Section>}
{activeModalSection === 'Payslip' && <Section icon={<FileWarning size={18} />} title="Payslip Issues" count={counts.payslipIssues}>
                        {!inbox?.payslipIssues?.length ? (
                            <EmptyState>No open payslip issues.</EmptyState>
                        ) : (
                            <div className="space-y-3">
                                {inbox.payslipIssues.map((issue, index) => (
                                    <div key={itemKey("payslip-issue", issue, index)} className="border border-[var(--border-color)] rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                        <div className="min-w-0">
                                            <p className="font-bold truncate">{employeeLabel(issue.employee)}</p>
                                            <p className="text-xs text-[var(--text-secondary)] truncate">{issue.employee?.team || "No team"} - {issue.payMonth || issue.periodKey || "Payslip"}</p>
                                            <p className="text-sm mt-2 font-semibold capitalize">{issue.issueType} mismatch</p>
                                            <p className="text-xs text-[var(--text-secondary)] mt-1 line-clamp-2">{issue.description}</p>
                                        </div>
                                        <ActionButtons
                                            disabled={processingId === issue._id}
                                            onApprove={() => handlePayslipIssue(issue, "resolved")}
                                            onReject={() => handlePayslipIssue(issue, "rejected")}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </Section>}

{activeModalSection === 'Offboarding' && <Section icon={<LogOut size={18} />} title="Offboarding Requests" count={counts.offboardingRequests}>
                        {!inbox?.offboardingRequests?.length ? (
                            <EmptyState>No active offboarding requests.</EmptyState>
                        ) : (
                            <div className="space-y-3">
                                {inbox.offboardingRequests.map((item, index) => (
                                    <div key={itemKey("offboarding", item, index)} className="border border-[var(--border-color)] rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <p className="font-bold break-words">{employeeLabel(item.employee)}</p>
                                                <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-black uppercase text-amber-700">
                                                    {item.status}
                                                </span>
                                            </div>
                                            <p className="text-xs text-[var(--text-secondary)] break-words">{item.employee?.team || "No team"} - requested {formatDate(item.createdAt)}</p>
                                            <p className="text-sm mt-2 font-semibold">Last working date: {formatDate(item.requestedLastWorkingDate)}</p>
                                            <p className="text-xs text-[var(--text-secondary)] mt-1 line-clamp-2 break-words">{item.reason}</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => navigate("/admin/offboarding")}
                                            className="h-9 px-4 rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] text-xs font-black uppercase tracking-wide shrink-0"
                                        >
                                            Review
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Section>}

{activeModalSection === 'Lunch Unlock' && <Section icon={<Coffee size={18} />} title="Lunch Break Unlock Requests" count={counts.lunchUnlockRequests || 0}>
                        {!inbox?.lunchUnlockRequests?.length ? (
                            <EmptyState>No pending lunch break unlock requests.</EmptyState>
                        ) : (
                            <div className="space-y-3">
                                {inbox.lunchUnlockRequests.map((item, index) => (
                                    <div key={itemKey("lunch-unlock", item, index)} className="border border-[var(--border-color)] rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-[var(--bg-secondary)]/50">
                                        <div className="min-w-0 space-y-1">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <p className="font-bold text-sm text-[var(--text-primary)]">{employeeLabel(item.employee)}</p>
                                                <span className="rounded-full bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] px-2.5 py-0.5 text-[11px] font-bold">
                                                    {item.employee?.team || "No team"}
                                                </span>
                                                <span className="text-xs text-[var(--text-secondary)] font-medium">
                                                    {formatDate(item.date)}
                                                </span>
                                            </div>
                                            <p className="text-xs text-[var(--text-secondary)]">
                                                Reason: <span className="font-semibold text-[var(--text-primary)]">{item.breakUnlockRequest?.reason || "Extra break requested"}</span>
                                            </p>
                                            <p className="text-[11px] text-[var(--text-secondary)]">
                                                Requested at: {formatTime(item.breakUnlockRequest?.requestedAt)}
                                            </p>
                                        </div>
                                        <ActionButtons
                                            disabled={processingId === item._id}
                                            onApprove={() => handleLunchUnlock(item, "approved")}
                                            onReject={() => handleLunchUnlock(item, "rejected")}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </Section>}
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    )}
                </AnimatePresence>
            </main>

            <AnimatePresence>
                <RequestHistoryDrawer
                    key="approved-history-drawer"
                    open={isApprovedRequestsOpen}
                    onClose={() => setIsApprovedRequestsOpen(false)}
                    requests={approvedRequests}
                    variant="approved"
                />
                <RequestHistoryDrawer
                    key="rejected-history-drawer"
                    open={isRejectedRequestsOpen}
                    onClose={() => setIsRejectedRequestsOpen(false)}
                    requests={rejectedRequests}
                    variant="rejected"
                />
            </AnimatePresence>

            <AnimatePresence>
                {selectedApprovedRequest && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setSelectedApprovedRequest(null)}
                            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        />
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 15 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 15 }}
                            transition={{ type: "spring", duration: 0.35 }}
                            className="relative z-10 w-full max-w-2xl rounded-3xl border border-emerald-200 bg-[var(--card-bg)] p-6 shadow-2xl"
                        >
                            <div className="absolute left-0 top-0 h-1 w-full rounded-t-3xl bg-emerald-500" />
                            <div className="flex items-start justify-between gap-4 border-b border-[var(--border-color)] pb-4">
                                <div className="min-w-0">
                                    <p className="text-xs font-black uppercase tracking-wide text-emerald-600">Approved request</p>
                                    <h3 className="mt-1 truncate text-xl font-black">{employeeLabel(selectedApprovedRequest.employee)}</h3>
                                    <p className="mt-1 text-xs font-bold text-[var(--text-secondary)]">
                                        {selectedApprovedRequest.employee?.team || "No team"} - {formatDate(selectedApprovedRequest.date)}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setSelectedApprovedRequest(null)}
                                    className="rounded-xl bg-emerald-50 p-2 text-emerald-700 transition hover:bg-emerald-100"
                                    title="Close"
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            {(() => {
                                const timing = getCorrectionTiming(selectedApprovedRequest);
                                return (
                                    <div className="mt-5 space-y-4">
                                        <div className="grid gap-3 sm:grid-cols-2">
                                            <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4">
                                                <p className="text-[11px] font-black uppercase tracking-wide text-[var(--text-secondary)]">{timing.label}</p>
                                                <p className="mt-2 text-sm font-black">{timing.value}</p>
                                            </div>
                                            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                                                <p className="text-[11px] font-black uppercase tracking-wide text-emerald-700">Approved by</p>
                                                <p className="mt-2 text-sm font-black text-emerald-800">{reviewerLabel(selectedApprovedRequest.reviewedBy) || "Admin"}</p>
                                                <p className="mt-1 text-xs font-bold text-emerald-700">{selectedApprovedRequest.reviewedAt ? formatDate(selectedApprovedRequest.reviewedAt) : "-"}</p>
                                            </div>
                                        </div>

                                        <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4">
                                            <p className="text-[11px] font-black uppercase tracking-wide text-[var(--text-secondary)]">Reason</p>
                                            <p className="mt-2 whitespace-pre-wrap break-words text-sm font-semibold leading-relaxed text-[var(--text-primary)]">
                                                {selectedApprovedRequest.reason || "No reason provided."}
                                            </p>
                                        </div>

                                        {selectedApprovedRequest.hrNotifiedAdminAt && (
                                            <div className="rounded-2xl border border-[var(--accent-soft)] bg-[var(--accent-light)] p-4 text-xs font-bold text-[var(--heading-secondary)]">
                                                HR message: This employee correction is still pending. Please see and rectify it.
                                                {selectedApprovedRequest.hrNotifiedAdminBy?.name ? ` - ${selectedApprovedRequest.hrNotifiedAdminBy.name}` : ""}
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* ===== MISSING EOD MODAL PORTAL (AnimatePresence) ===== */}
            <AnimatePresence>
                {isMissingEodModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        {/* Backdrop */}
                        <motion.div 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsMissingEodModalOpen(false)}
                            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        />
                        {/* Modal Box */}
                        <motion.div 
                            initial={{ scale: 0.95, opacity: 0, y: 15 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 15 }}
                            transition={{ type: "spring", duration: 0.4 }}
                            className="bg-[var(--card-bg)] border border-[var(--border-color)] w-full max-w-lg rounded-3xl p-6 shadow-2xl z-10 relative flex flex-col max-h-[85vh] overflow-hidden"
                        >
                            <div className="absolute top-0 left-0 w-full h-1 bg-[var(--accent-primary)]"></div>
                            
                            {/* Modal Header */}
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-[var(--accent-primary)]/10 text-[var(--accent-primary)] flex items-center justify-center shrink-0">
                                        <FileWarning size={20} />
                                    </div>
                                    <div>
                                        <h3 className="font-extrabold text-lg text-[var(--text-primary)]">Missing EOD Reports</h3>
                                        <p className="text-xs text-[var(--text-secondary)] mt-0.5 font-semibold">
                                            For {formatDate(missingEodFilterType === "selected" ? date : missingEodCustomDate)} • {filteredMissingEods.length} employee{filteredMissingEods.length === 1 ? "" : "s"}
                                        </p>
                                    </div>
                                </div>
                                <button 
                                    onClick={() => setIsMissingEodModalOpen(false)}
                                    className="p-1.5 rounded-xl hover:bg-[var(--bg-secondary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-all"
                                >
                                    <X size={18} />
                                </button>
                            </div>

                             {/* Search Bar inside Modal */}
                            {inbox?.missingEod?.length > 0 && (
                                <div className="relative mb-3 shrink-0">
                                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] opacity-60" size={16} />
                                    <input 
                                        type="text"
                                        placeholder="Search employee by name, team, or ID..." 
                                        value={missingEodSearch}
                                        onChange={e => setMissingEodSearch(e.target.value)}
                                        className="w-full bg-[var(--bg-primary)] border border-[var(--border-color)] pl-10 pr-4 py-2.5 rounded-2xl outline-none text-xs sm:text-sm font-semibold transition-all focus:border-[var(--accent-primary)]/60 focus:ring-2 focus:ring-[var(--accent-primary)]/10"
                                    />
                                </div>
                            )}

                            {/* Date Filter Row */}
                            {inbox?.missingEod?.length > 0 && (
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 shrink-0 bg-[var(--bg-primary)] p-2.5 rounded-2xl border border-[var(--border-color)]">
                                    <div className="flex gap-1">
                                        <button
                                            type="button"
                                            onClick={() => setMissingEodFilterType("selected")}
                                            className={`px-3.5 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${
                                                missingEodFilterType === "selected"
                                                    ? "bg-[var(--accent-primary)] text-white shadow-sm"
                                                    : "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-secondary)]"
                                            }`}
                                        >
                                            Today
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setMissingEodFilterType("custom");
                                                if (!missingEodCustomDate) setMissingEodCustomDate(date);
                                            }}
                                            className={`px-3.5 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${
                                                missingEodFilterType === "custom"
                                                    ? "bg-[var(--accent-primary)] text-white shadow-sm"
                                                    : "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-secondary)]"
                                            }`}
                                        >
                                            Custom Date
                                        </button>
                                    </div>
                                    {missingEodFilterType === "custom" && (
                                        <div className="flex items-center gap-2 shrink-0">
                                            <input
                                                type="date"
                                                value={missingEodCustomDate}
                                                onChange={(e) => setMissingEodCustomDate(e.target.value)}
                                                className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-xl px-3 py-1.5 text-xs font-black outline-none focus:border-[var(--accent-primary)]/50"
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Modal Body / Scrollable Content */}
                            <div className="flex-1 overflow-y-auto space-y-2.5 pr-1 -mr-2">
                                {!inbox?.missingEod?.length ? (
                                    <div className="border border-dashed border-[var(--border-color)] bg-[var(--bg-secondary)] rounded-2xl p-8 text-center text-sm text-[var(--text-secondary)] font-semibold">
                                        Everyone has submitted EOD for this date.
                                    </div>
                                ) : filteredMissingEods.length === 0 ? (
                                    <div className="border border-dashed border-[var(--border-color)] bg-[var(--bg-secondary)] rounded-2xl p-8 text-center text-sm text-[var(--text-secondary)] font-semibold">
                                        No employee matches "{missingEodSearch}"
                                    </div>
                                ) : (
                                    filteredMissingEods.map((employee) => (
                                        <div key={employee._id} className="border border-[var(--border-color)] rounded-2xl p-4 flex items-center justify-between gap-3 hover:border-[var(--accent-primary)]/30 transition-all duration-300">
                                            <div className="min-w-0">
                                                <p className="font-bold truncate text-sm text-[var(--text-primary)]">{employeeLabel(employee)}</p>
                                                <p className="text-[11px] text-[var(--text-secondary)] truncate font-semibold">
                                                    {employee.team || "No team"} • {employee.employeeId || employee.email}
                                                </p>
                                                <div className="flex flex-wrap gap-1 mt-1.5">
                                                    {employee.missingDates && employee.missingDates.length > 0 ? (
                                                        <>
                                                            {employee.missingDates.slice(0, 5).map((d) => (
                                                                <span key={d} className="text-[9px] bg-red-500/10 text-red-500 px-1.5 py-0.5 rounded-md font-black tracking-wide shrink-0">
                                                                    Missing EOD for {formatShortDate(d)}
                                                                </span>
                                                            ))}
                                                            {employee.missingDates.length > 5 && (
                                                                <span className="text-[9px] bg-red-500/20 text-red-600 px-1.5 py-0.5 rounded-md font-black tracking-wide shrink-0" title={employee.missingDates.slice(5).map(formatShortDate).join(', ')}>
                                                                    + {employee.missingDates.length - 5} more
                                                                </span>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <span className="text-[9px] bg-red-500/10 text-red-500 px-1.5 py-0.5 rounded-md font-black tracking-wide shrink-0">
                                                            Missing EOD for {formatShortDate(date)}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setIsMissingEodModalOpen(false);
                                                    navigate("/admin/eod");
                                                }}
                                                className="h-8.5 px-3.5 rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] text-xs font-black uppercase tracking-wider flex items-center gap-2 shrink-0 hover:bg-[var(--accent-primary)] hover:text-white hover:border-[var(--accent-primary)] transition-all duration-300"
                                            >
                                                <ClipboardList size={13} />
                                                EOD
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                            
                            {/* Modal Footer */}
                            <div className="mt-5 pt-3 border-t border-[var(--border-color)] flex justify-end shrink-0">
                                <button
                                    onClick={() => setIsMissingEodModalOpen(false)}
                                    className="px-4 py-2 rounded-xl bg-[var(--bg-secondary)] border border-[var(--border-color)] hover:bg-[var(--bg-primary)] text-xs font-black uppercase tracking-wider text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-all"
                                >
                                    Close
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ApprovalInbox;


